import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import countriesPhoneList from "countries-phone-masks"
import { MaskedInput as MaskedInputComponent } from "antd-mask-input"
import { Form, Input, Space, Select, Typography } from "antd"
import { getCountryCodeForRegionCode, parsePhoneNumber } from "awesome-phonenumber"

import { W0 } from "@components/constants"
import { COUNTRY_US } from "@components/Domain/Constants"

import getCountryCodesOptions from "./helpers/getCountryCodesOptions"

import "./PhoneInput.css"

const { Compact } = Space
const { Text } = Typography

const ERROR_INVALID_NUMBER = "Number is invalid"

const US_MASK = "(000)000-0000"

const REGEX_REPLACE_SYMBOLS = /\/|-|_|\(|\)/g

const countryCodesOptions = getCountryCodesOptions()


const PhoneInput = ({
  form,
  path,
  itemProps,
  inputProps,
}) => {
  const maskRef = useRef()
  const [ inputMask, setInputMask ] = useState(US_MASK)
  const [ mask, setMask ] = useState(US_MASK)
  const { name, initialValue, rules, label, ...restItemProps } = itemProps

  const codeField = `_code_${name.slice(-1)}`
  const inputField = `_${name.slice(-1)}`

  const inputName = [...name.slice(0, -1), inputField]
  const inputPath = [...path.slice(0, -1), inputField]

  const codeName = [...name.slice(0, -1), codeField]
  const codePath = [...path.slice(0, -1), codeField]

  const sourceValue = Form.useWatch(path, form)
  const inputSourceValue = Form.useWatch(inputName, form)

  const isRequired = rules.find(rule => rule.required)

  useEffect(() => {
    setValue()
  }, [ sourceValue ])

  useEffect(() => {
    setValue()
  }, [inputSourceValue])

  const getFormattedValue = value => {
    const maskedValue = []
    let maskedSymbolIndex = 0
    for (let i = 0; i <= inputMask.length; i++) {
      const isMaskSymbol = ["(", ")", "-"].includes(inputMask[i])

      if (isMaskSymbol === undefined) {
        maskedValue.push(inputMask[i])
        // eslint-disable-next-line no-continue
        continue
      }

      maskedValue.push(value[maskedSymbolIndex])
      maskedSymbolIndex++
    }

    return maskedValue.join("")
  }

  const setMaskByCountry = countryCode => {
    const { mask: countryMask } = countriesPhoneList.find(({ iso }) => countryCode === iso)
    const isMaskArray = Array.isArray(countryMask)

    setMask(countryMask)

    if (isMaskArray) {
      const exampleMaskLengthList = countryMask.map(item => item.replace(REGEX_REPLACE_SYMBOLS, "0").length)
      const maxLength = Math.max(...exampleMaskLengthList)

      setInputMask("0".repeat(maxLength))
    } else {
      const formattedMask = countryMask.replace(/0/g, "&").replace(/#/g, "!")
      setInputMask(formattedMask)
    }
  }

  const setValue = () => {
    const value = form.getFieldValue(path)

    if (!value) {
      return
    }

    const { valid, regionCode, number: { significant } } = parsePhoneNumber(`+${value}`)

    if (valid) {
      form.setFieldValue(codePath, regionCode)
      setMaskByCountry(regionCode)
      const maskedValue = getFormattedValue(significant)

      form.setFieldValue(inputPath, maskedValue)
    }
  }

  const onChange = ({ maskedValue }) => {
    const unmaskedValue = maskedValue.replace(/\(|\)|_|-/g, "")

    const hasValue = unmaskedValue.length > 0

    if (!hasValue) {
      form.setFieldValue(path, undefined)
      return
    }

    const regionCode = form.getFieldValue(codePath)
    const countryCode = getCountryCodeForRegionCode(regionCode)
    const fullValue = `${countryCode}${unmaskedValue}`
    form.setFieldValue(path, fullValue)
  }

  const itemRules = [
    {
      message: ERROR_INVALID_NUMBER,
      validator: (_, value) => {
        const formattedMask = mask.replace(/#/g, '_')

        const isEmpty = !value || value.length === 0 || formattedMask === value

        if (!isRequired && isEmpty) {
          return Promise.resolve()
        }

        const regionCode = form.getFieldValue(codePath)
        const { code } = countriesPhoneList.find(({ iso }) => regionCode === iso)

        const test = parsePhoneNumber(`${code}${value}`)
        const { valid } = test

        if (!valid) {
          throw new Error(ERROR_INVALID_NUMBER)
        }

        return Promise.resolve()
      }
    },
    ...rules
  ]

  const phoneItemProps = {
    ...restItemProps,
    rules: itemRules
  }

  const onChangeCountry = countryCode => {
    const { code } = countriesPhoneList.find(({ iso }) => countryCode === iso)
    setMaskByCountry(countryCode)

    const formattedCode = code.replace("+", "")

    form.setFieldValue(inputPath, "")
    form.setFieldValue(path, formattedCode)
  }

  const labelClass = isRequired ? "item-required" : ""
  const labelStyle = {
    paddingBottom: W0
  }

  const definitions = {
    '&': /^[0]{1}$/,
    '!': /^[0-9]{1}$/,
  }

  const maskComponent = useMemo(() => <MaskedInputComponent
    ref={maskRef}
    mask={inputMask}
    style={{ width: 160 }}
    allowClear={true}
    definitions={definitions}
    onChange={onChange}
    {...inputProps}
    placeholder=""
  />, [sourceValue, inputMask])

  return (
    <div>
      <Form.Item
        key="hidden"
        name={name}
        hidden={true}
        initialValue={initialValue}
      >
        <Input />
      </Form.Item>

      <div className="phone-input">
        <div>
          <div
            className={labelClass}
            style={labelStyle}
          >
            <Text strong>{label}</Text>
          </div>
        </div>

        <Compact>
          <Form.Item
            key="code"
            name={codeName}
            initialValue={COUNTRY_US}
          >
            <Select
              style={{ width: 110 }}
              options={countryCodesOptions}
              onChange={onChangeCountry}
            />
          </Form.Item>

          <Form.Item
            name={inputName}
            rules={itemRules}
            className="mask-input"
            {...phoneItemProps}
          >
            {maskComponent}
          </Form.Item>
        </Compact>
      </div>
    </div>
  )
}

PhoneInput.propTypes = {
  form: PropTypes.shape().isRequired,
  path: PropTypes.arrayOf(PropTypes.string).isRequired,
  itemProps: PropTypes.shape().isRequired,
  inputProps: PropTypes.shape().isRequired,
}

export default PhoneInput
